import { FunctionComponent, useEffect, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import classnames from 'classnames'

import Icon from 'components/ui/icon'
import LineChart, { LineChartPoint } from 'components/charts/lineChart'
import SegmentedControl from 'components/ui/segmentedControl'
import { Transfer, TransferStatus } from '../types'
import { TRANSFER_STATUS_META } from '../transferStatus'
import TransferReport, { PendingTab } from './transferReport'
import ReportTools from './reportTools'
import MoreMenu from './moreMenu'
import transfersCss from '../styles.module.scss'

export type TransferPanelView = 'chart' | 'detail'

type TransferPanelProps = {
  status: TransferStatus
  chartPoints: LineChartPoint[]
  transfers: Transfer[]
  onDownload: () => void
  view: TransferPanelView
  onViewChange: (view: TransferPanelView) => void
}

/** Card that toggles between the monthly chart and the detail table. */
const TransferPanel: FunctionComponent<TransferPanelProps> = ({
  status,
  chartPoints,
  transfers,
  onDownload,
  view,
  onViewChange,
}) => {
  const [fullscreen, setFullscreen] = useState(false)
  const [search, setSearch] = useState('')
  const [pendingTab, setPendingTab] = useState<PendingTab>('particular')
  const meta = TRANSFER_STATUS_META[status]
  const isDetail = view === 'detail'
  const isPending = status === TransferStatus.PENDENTE

  useEffect(() => {
    setSearch('')
    setPendingTab('particular')
  }, [status])

  const downloadLabel = `Baixar repasses ${meta.downloadSuffix}`
  // Particular/convênios only matter for the detail table.
  const pending =
    isPending && isDetail ? { value: pendingTab, onChange: setPendingTab } : undefined

  const tools = (
    <ReportTools
      search={search}
      onSearchChange={setSearch}
      onDownload={onDownload}
      downloadLabel={downloadLabel}
      pending={isPending ? { value: pendingTab, onChange: setPendingTab } : undefined}
    />
  )

  // Same button in both views; sits to the left of the view switch on phones.
  const moreMenu = (
    <MoreMenu downloadLabel={downloadLabel} onDownload={onDownload} pending={pending} />
  )

  return (
    <section
      className={classnames('card', transfersCss.panel, isDetail && transfersCss.panelFill)}
    >
      <div className="card-head">
        <div className={transfersCss.panelTitle}>
          <h2 className="card-title">Repasses - {meta.title}</h2>
          {/* Hidden on phones and tablets to keep the header on one row (see the chart caption). */}
          <p className="card-desc d-none d-lg-block">
            {isDetail
              ? 'Atendimentos que compõem o valor selecionado'
              : 'Valores por mês, de acordo com a data do procedimento/atendimento'}
          </p>
        </div>
        <div className={transfersCss.panelActions}>
          {isDetail && (
            <Button
              variant="ghost"
              size="sm"
              aria-label="Tela cheia"
              title="Tela cheia"
              onClick={() => setFullscreen(true)}
            >
              <Icon name="expand" />
              <span className="d-none d-lg-inline">Tela cheia</span>
            </Button>
          )}
          <SegmentedControl<TransferPanelView>
            ariaLabel="Visualização"
            value={view}
            onChange={onViewChange}
            options={[
              {
                value: 'chart',
                title: 'Gráfico',
                label: (
                  <>
                    <Icon name="chart" />
                    <span className="d-none d-lg-inline">Gráfico</span>
                  </>
                ),
              },
              {
                value: 'detail',
                title: 'Detalhamento',
                label: (
                  <>
                    <Icon name="list" />
                    <span className="d-none d-lg-inline">Detalhamento</span>
                  </>
                ),
              },
            ]}
          />
          {isDetail && tools}
          {moreMenu}
        </div>
      </div>

      {!isDetail ? (
        <div className={transfersCss.chartArea}>
          <LineChart
            points={chartPoints}
            color={meta.color}
            ariaLabel={`Repasses por mês — ${meta.title}`}
          />
          <p className={classnames('card-desc d-lg-none', transfersCss.chartCaption)}>
            Valores por mês, de acordo com a data do procedimento/atendimento
          </p>
        </div>
      ) : (
        !fullscreen && (
          <TransferReport
            transfers={transfers}
            status={status}
            search={search}
            pendingTab={pendingTab}
          />
        )
      )}

      <Modal
        show={fullscreen}
        onHide={() => setFullscreen(false)}
        fullscreen="lg-down"
        dialogClassName={transfersCss.fullscreenDialog}
      >
        <Modal.Header closeButton className={transfersCss.modalHeader}>
          <Modal.Title>Repasses - {meta.title}</Modal.Title>
          <div className={transfersCss.modalTools}>
            {tools}
            {moreMenu}
          </div>
        </Modal.Header>
        <Modal.Body className={transfersCss.fullscreenBody}>
          <TransferReport
            transfers={transfers}
            status={status}
            search={search}
            pendingTab={pendingTab}
          />
        </Modal.Body>
      </Modal>
    </section>
  )
}

export default TransferPanel
